import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { filterRowsForCurrentScope, getCenterScopedUrl, centerApis, shouldUseCenterScopedApi } from "../../utils/centerScope";
import { getStoredContext } from "../../utils/contextStorage";
import { getAssignedCenterId } from "../../utils/userSession";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

export default function MobilizationManagementPage() {
    const token = localStorage.getItem("token");
    const assignedCenterId = getAssignedCenterId();
    const selectedProjectId = getStoredContext()?.project || "";

    const store = useDataStore();
    const CACHE_KEY = "mobilizations";

    /* ================= COMMON ================= */
    const [msg, setMsg] = useState({ type: "", text: "" });
    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }),
        [token]
    );

    const safeJson = async (res) => {
        const t = await res.text();
        try {
            return t ? JSON.parse(t) : [];
        } catch {
            return [];
        }
    };

    /* ================= EXCEL UPLOAD ================= */
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    /* uploadProgress: { uploaded, total, chunk, totalChunks } | null */

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;   /* concurrent uploads at a time */

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select Excel file");

        /* ── Parse Excel on client as array-of-arrays (no Excel reconstruction needed) ── */
        let dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            dataRows = raw.slice(1); // skip header row
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }

        if (!dataRows.length) return flash("err", "Excel file is empty");

        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

        /* shared mutable counters (updated inside parallel tasks) */
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";

        /* sends one JSON chunk directly — no Excel reconstruction */
        const uploadOneChunk = async (i) => {
            const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);

            const res = await fetch(
                `${BASE_URL}/api/mobilizations/upload-json?actor=Rahul`,
                {
                    method: "POST",
                    headers: { ...authHeaders, "Content-Type": "application/json" },
                    body: JSON.stringify(chunkRows),
                }
            );

            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);

            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }

            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);

            const inserted = data?.inserted ?? 0;
            const skipped = data?.skipped ?? 0;
            const skippedRows = data?.skippedRows ?? [];

            console.log(`[Chunk ${i + 1}/${totalChunks}] Inserted: ${inserted} | Skipped: ${skipped}`);
            if (skippedRows.length > 0) {
                console.group(`[Chunk ${i + 1}] Skipped rows (${skippedRows.length}):`);
                skippedRows.forEach(reason => console.log(reason));
                console.groupEnd();
            }

            return { inserted, skipped };
        };

        /* process in batches of PARALLEL concurrent requests */
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);

            setUploadProgress({
                uploaded: uploadedSoFar,
                total: totalRows,
                chunk: batchEnd,
                totalChunks,
            });

            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));

            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }

            setUploadProgress({
                uploaded: uploadedSoFar,
                total: totalRows,
                chunk: batchEnd,
                totalChunks,
                error: failed,
            });
        }

        setUploading(false);

        if (!failed) {
            console.log(`[Upload Summary] Total Inserted: ${totalInserted} | Total Skipped: ${totalSkipped} | Total Rows: ${totalRows}`);
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllMobilizations();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    /* ================= TABLE ================= */
    const [rows, setRows] = useState(() => store.getRows(CACHE_KEY));
    const [courseOptions, setCourseOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [totalCount, setTotalCount] = useState(null);
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);

    const resolveScopedUrl = (resource) => {
        if (!shouldUseCenterScopedApi() || !assignedCenterId) {
            return getCenterScopedUrl(BASE_URL, `/api/${resource}`);
        }

        if (selectedProjectId) {
            if (resource === "mobilizations") {
                return centerApis.mobilizationsByProject(assignedCenterId, selectedProjectId);
            }

            if (resource === "courses") {
                return centerApis.coursesByProject(assignedCenterId, selectedProjectId);
            }
        }

        if (resource === "mobilizations") {
            return centerApis.mobilizations(assignedCenterId);
        }

        if (resource === "courses") {
            return centerApis.courses(assignedCenterId);
        }

        return getCenterScopedUrl(BASE_URL, `/api/${resource}`);
    };

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setRows(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        setAllLoaded(false);
        try {
            let items;
            let fetchedAll = false;
            if (!shouldUseCenterScopedApi() || !assignedCenterId) {
                // Admin: use page endpoint — returns only 100 rows instantly
                const res = await fetch(
                    `${BASE_URL}/api/mobilizations/page?page=0&size=100`,
                    { headers: authHeaders }
                );
                const json = await safeJson(res);
                items = Array.isArray(json) ? json : (json.content ?? []);
            } else {
                // Center-scoped user: their subset is small, fetch all
                const res = await fetch(resolveScopedUrl("mobilizations"), { headers: authHeaders });
                items = await safeJson(res);
                fetchedAll = true;
            }
            const filtered = filterRowsForCurrentScope(Array.isArray(items) ? items : []);
            store.setRows(CACHE_KEY, filtered, fetchedAll);
            setRows(filtered);
            if (fetchedAll) setAllLoaded(true);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllMobilizations = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(resolveScopedUrl("mobilizations"), { headers: authHeaders });
            const all = filterRowsForCurrentScope(await safeJson(res));
            store.markAllLoaded(CACHE_KEY, all);
            setRows(all);
            setAllLoaded(true);
            setVisibleCount(all.length);
        } catch {
            // keep existing rows on failure
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        fetchFirst100();
    }, []);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.mobilizations ?? null); })
            .catch(() => { });
    }, []);

    const fetchCourses = async () => {
        try {
            const res = await fetch(resolveScopedUrl("courses"), {
                headers: authHeaders,
            });
            const data = await safeJson(res);
            setCourseOptions(Array.isArray(data) ? data : []);
        } catch {
            setCourseOptions([]);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, [selectedProjectId]);

    useEffect(() => { setVisibleCount(100); }, [search]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return rows.filter(
            (r) =>
                r.beneficiaryId?.toLowerCase().includes(q) ||
                r.name?.toLowerCase().includes(q) ||
                r.projectName?.toLowerCase().includes(q)
        );
    }, [rows, search]);

    /* ================= EDIT ================= */
    const emptyForm = {
        beneficiaryId: "",

        // department: "",
        dateOfMobilization: "",

        centreName: "",
        projectName: "",

        mobilizationType: "",
        mobilizerName: "",
        areaLocation: "",

        salutation: "",
        name: "",
        gender: "",
        age: "",

        mobileNo: "",
        whatsappNumber: "",
        aadharNumber: "",
        emailId: "",

        highestEducationQualification: "",
        guardianName: "",

        workExperience: "",
        willingToTakeJob: "",
        willingToMigrate: "",

        enrolledLast6Months: "",
        interestedCourseName: "",
        hearAboutUs: "",
        counselled: "",
        rocVerificationStatus: ""
    };


    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const openEdit = (row) => {
        setEditingId(row.beneficiaryId);

        setForm({
            beneficiaryId: row.beneficiaryId ?? "",

            // department: row.department ?? "",
            dateOfMobilization: row.dateOfMobilization ?? "",

            centreName: row.centreName ?? "",
            projectName: row.projectName ?? "",

            mobilizationType: row.mobilizationType ?? "",
            mobilizerName: row.mobilizerName ?? "",
            areaLocation: row.areaLocation ?? "",

            salutation: row.salutation ?? "",
            name: row.name ?? "",
            gender: row.gender ?? "",
            age: row.age ?? "",

            mobileNo: row.mobileNo ?? "",
            whatsappNumber: row.whatsappNumber ?? "",
            aadharNumber: row.aadharNumber ?? "",
            emailId: row.emailId ?? "",

            highestEducationQualification: row.highestEducationQualification ?? "",
            guardianName: row.guardianName ?? "",

            workExperience: row.workExperience ?? "",
            willingToTakeJob: row.willingToTakeJob ?? "",
            willingToMigrate: row.willingToMigrate ?? "",

            enrolledLast6Months: row.enrolledLast6Months ?? "",
            interestedCourseName: row.interestedCourseName ?? "",
            hearAboutUs: row.hearAboutUs ?? "",
            counselled: row.counselled ?? "",
            rocVerificationStatus: row.rocVerificationStatus ?? ""
        });
    };

    const updateMobilization = async () => {
        await fetch(
            `${BASE_URL}/api/mobilizations?beneficiaryId=${encodeURIComponent(
                editingId
            )}&actor=Rahul`,
            {
                method: "PUT",
                headers: jsonHeaders,
                body: JSON.stringify(form),
            }
        );
        flash("ok", "Mobilization updated");
        setEditingId(null);
        store.invalidate(CACHE_KEY);
        fetchAllMobilizations();
    };

    /* ================= DELETE ================= */
    const deleteMobilization = async (beneficiaryId) => {
        if (!window.confirm("Deactivate this mobilization?")) return;

        await fetch(
            `${BASE_URL}/api/mobilizations?beneficiaryId=${encodeURIComponent(
                beneficiaryId
            )}&actor=Rahul`,
            {
                method: "DELETE",
                headers: authHeaders,
            }
        );
        flash("ok", "Mobilization deleted");
        store.invalidate(CACHE_KEY);
        fetchAllMobilizations();
    };

    /* ================= AUDIT ================= */
    const [auditOpen, setAuditOpen] = useState(false);
    const [auditRows, setAuditRows] = useState([]);
    const [auditBeneficiary, setAuditBeneficiary] = useState("");

    const openAuditAll = async () => {
        setAuditOpen(true);
        setAuditBeneficiary("");
        const res = await fetch(
            `${BASE_URL}/api/mobilization-audits`,
            { headers: authHeaders }
        );
        setAuditRows(await safeJson(res));
    };

    const openAuditByBeneficiary = async (beneficiaryId) => {
        setAuditOpen(true);
        setAuditBeneficiary(beneficiaryId);
        const res = await fetch(
            `${BASE_URL}/api/mobilization-audits/by-beneficiary?beneficiaryId=${encodeURIComponent(
                beneficiaryId
            )}`,
            { headers: authHeaders }
        );
        setAuditRows(await safeJson(res));
    };


    const FormField = ({ label, value, onChange, type = "text" }) => (
        <div className="field">
            <label>{label}</label>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );

    const [exporting, setExporting] = useState(false);

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (shouldUseCenterScopedApi() && assignedCenterId) {
                params.set("centerId", assignedCenterId);
                if (selectedProjectId) params.set("projectId", selectedProjectId);
            }
            const url = `${BASE_URL}/api/mobilizations/export${params.toString() ? "?" + params.toString() : ""}`;
            const res = await fetch(url, { headers: authHeaders });
            if (!res.ok) throw new Error(`Export failed (${res.status})`);
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "mobilizations.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
        } catch (err) {
            flash("err", err.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/mobilization_template.xlsx"; // local path
        a.download = "mobilization_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };


    /* ================= UI ================= */
    return (
        <div className="cmp-page">
            <div className="header">
                <h1>Mobilization Management</h1>
                {/* <button className="btn" onClick={openAuditAll}>
                    View All Audits
                </button> */}
            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {/* Chunk upload progress */}
            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            {/* Upload */}
            <div className="card grid-3">
                <input type="file" onChange={(e) => setFile(e.target.files[0])} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }} />
                <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                    {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                </button>

                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Excel Template
                </button>
            </div>

            {/* Table */}
            <div className="card">
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                        className="search"
                        placeholder="Search beneficiary / name / project..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ flex: "0 0 70%", marginBottom: 0 }}
                    />
                    <button
                        className="btn btn-outline"
                        onClick={exportExcel}
                        style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
                        disabled={exporting}
                    >
                        {exporting ? "Processing..." : "Export All Data"}
                    </button>
                </div>
                {totalCount !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                        Total: {totalCount.toLocaleString()}
                    </div>
                )}

                <div className="table-wrap">
                    <table className="ep-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Beneficiary ID</th>

                                {/* <th>Department</th> */}
                                <th>Date Of Mobilization</th>



                                <th>Mobilization Type</th>
                                <th>Mobilizer Name</th>
                                <th>Area / Location</th>

                                <th>Salutation</th>
                                <th>Name</th>
                                <th>Gender</th>
                                <th>Age</th>

                                <th>Mobile No</th>
                                <th>WhatsApp No</th>
                                <th>Aadhar No</th>
                                <th>Email</th>

                                <th>Highest Education</th>
                                <th>Guardian Name</th>

                                <th>Work Experience</th>
                                <th>Willing To Take Job</th>
                                <th>Willing To Migrate</th>

                                <th>Enrolled Last 6 Months</th>
                                <th>Interested Course</th>
                                <th>Heard About Us</th>
                                <th>Counselled</th>
                                {/* <th>ROC Verification</th> */}

                                <th className="actions">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.slice(0, visibleCount).map((r, i) => (
                                <tr key={r.beneficiaryId}>
                                    <td>{i + 1}</td>
                                    <td>{r.beneficiaryId}</td>

                                    <td>{r.dateOfMobilization}</td>



                                    <td>{r.mobilizationType}</td>
                                    <td>{r.mobilizerName}</td>
                                    <td>{r.areaLocation}</td>

                                    <td>{r.salutation}</td>
                                    <td>{r.name}</td>
                                    <td>{r.gender}</td>
                                    <td>{r.age}</td>

                                    <td>{r.mobileNo}</td>
                                    <td>{r.whatsappNumber}</td>
                                    <td>{r.aadharNumber}</td>
                                    <td>{r.emailId}</td>

                                    <td>{r.highestEducationQualification}</td>
                                    <td>{r.guardianName}</td>

                                    <td>{r.workExperience}</td>
                                    <td>{r.willingToTakeJob}</td>
                                    <td>{r.willingToMigrateOtherState}</td>

                                    <td>{r.enrolledLast6Months}</td>
                                    <td>{r.interestedCourseName}</td>
                                    <td>{r.hearAboutUs}</td>
                                    <td>{r.counselled}</td>
                                    {/* <td>{r.rocVerificationStatus}</td> */}

                                    <td className="actions">
                                        {/* <button className="btn btn-sm" onClick={() => openEdit(r)}>
                                            Edit
                                        </button> */}
                                        {/* <button
                                            className="btn btn-sm"
                                            onClick={() => openAuditByBeneficiary(r.beneficiaryId)}
                                        >
                                            Audit
                                        </button> */}
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => deleteMobilization(r.beneficiaryId)}
                                        >
                                            Deactivate
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan="27" className="muted">
                                        {loading ? "Loading..." : "No mobilizations found"}
                                    </td>
                                </tr>
                            )}
                            {!allLoaded && !loading && (
                                <tr>
                                    <td colSpan="27" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={fetchAllMobilizations}
                                            disabled={loadingAll}
                                        >
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {allLoaded && filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan="27" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={() => setVisibleCount((c) => c + 100)}
                                        >
                                            Show More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>

                    </table>
                </div>
            </div>

            {/* EDIT MODAL */}
            {editingId &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setEditingId(null)}>
                        <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
                            <h2>Edit Mobilization</h2>

                            <div className="form-grid">

                                <FormField label="Department" value={form.department}
                                    onChange={(v) => setForm(p => ({ ...p, department: v }))} />

                                <FormField label="Date Of Mobilization" type="date"
                                    value={form.dateOfMobilization}
                                    onChange={(v) => setForm(p => ({ ...p, dateOfMobilization: v }))} />

                                <FormField label="Centre Name" value={form.centreName}
                                    onChange={(v) => setForm(p => ({ ...p, centreName: v }))} />

                                <FormField label="Project Name" value={form.projectName}
                                    onChange={(v) => setForm(p => ({ ...p, projectName: v }))} />

                                <FormField label="Mobilization Type" value={form.mobilizationType}
                                    onChange={(v) => setForm(p => ({ ...p, mobilizationType: v }))} />

                                <FormField label="Mobilizer Name" value={form.mobilizerName}
                                    onChange={(v) => setForm(p => ({ ...p, mobilizerName: v }))} />

                                <FormField label="Name" value={form.name}
                                    onChange={(v) => setForm(p => ({ ...p, name: v }))} />

                                <FormField label="Gender" value={form.gender}
                                    onChange={(v) => setForm(p => ({ ...p, gender: v }))} />

                                <FormField label="Age" type="number" value={form.age}
                                    onChange={(v) => setForm(p => ({ ...p, age: v }))} />

                                <FormField label="Mobile No" value={form.mobileNo}
                                    onChange={(v) => setForm(p => ({ ...p, mobileNo: v }))} />

                                <FormField label="WhatsApp Number" value={form.whatsappNumber}
                                    onChange={(v) => setForm(p => ({ ...p, whatsappNumber: v }))} />

                                <FormField label="Email ID" value={form.emailId}
                                    onChange={(v) => setForm(p => ({ ...p, emailId: v }))} />

                                <div className="field">
                                    <label>Interested Course</label>
                                    <select
                                        value={form.interestedCourseName || ""}
                                        onChange={(e) => setForm((p) => ({ ...p, interestedCourseName: e.target.value }))}
                                    >
                                        <option value="">Select Course</option>
                                        {courseOptions.map((course) => (
                                            <option key={course.courseId} value={course.courseName || course.courseId}>
                                                {course.courseName || course.courseId}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {/* 
                                <FormField label="ROC Verification Status" value={form.rocVerificationStatus}
                                    onChange={(v) => setForm(p => ({ ...p, rocVerificationStatus: v }))} /> */}

                            </div>

                            <div className="modal-actions">
                                <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
                                <button className="btn btn-primary" onClick={updateMobilization}>Save</button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )
            }


            {/* AUDIT MODAL */}
            {auditOpen &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
                        <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
                            <h2>Mobilization Audit Logs</h2>
                            {auditBeneficiary && (
                                <div className="muted">
                                    Beneficiary: <b>{auditBeneficiary}</b>
                                </div>
                            )}
                            <pre>{JSON.stringify(auditRows, null, 2)}</pre>
                        </div>
                    </div>,
                    document.body
                )}

            {/* INLINE CSS */}
            <style>{`
        .input-file {
                 border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 12px",
        background: "#fff",}
            .table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.ep-table {
  width: max-content;      /* KEY */
  min-width: 2600px;       /* enough for all columns */
  border-collapse: collapse;
}

.ep-table th,
.ep-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  white-space: nowrap;     /* PREVENT WRAP */
}

.ep-table th {
  background: #fcfcff;
  position: sticky;
  top: 0;
  z-index: 2;
}

/* Sticky Actions Column */
.ep-table td.actions,
.ep-table th.actions {
  position: sticky;
  right: 0;
  background: #fff;
  box-shadow: -6px 0 8px rgba(0,0,0,0.05);
  z-index: 3;
}



      .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
      .header { display:flex; justify-content:space-between; align-items:center }
      h1 { font-size:26px; font-weight:900 }

      .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
      .flash-ok { background:#ecfdf5; color:#065f46 }
      .flash-err { background:#fef2f2; color:#991b1b }

      .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
      .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

      .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
      .btn-primary { background:#2563eb; color:#fff }
      .btn-sm { padding:6px 10px; font-size:12px }
      .btn-danger { color:#991b1b; border-color:#fca5a5 }

      .search { margin-bottom:10px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

      .table-wrap { overflow-x:auto }
      .ep-table { width:100%; min-width:1200px; border-collapse:collapse }
      th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
      th { background:#fcfcff; text-align:left }
      td.actions { position:sticky; right:0; background:#fff }

      .muted { color:#6b7280; font-weight:700 }

      .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
      .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
      .ep-modal.wide { width:1000px }

      .form-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:12px }
      .field { display:flex; flex-direction:column; gap:6px }
      .field label { font-size:11px; font-weight:800; color:#6b7280 }
      .field input, .field select { padding:10px; border-radius:10px; border:1px solid #e5e7eb }

      .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
      `}</style>
        </div>
    );
}



// import React, { useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";
// import { filterRowsForCurrentScope, getCenterScopedUrl, centerApis, shouldUseCenterScopedApi } from "../../utils/centerScope";
// import { getStoredContext } from "../../utils/contextStorage";
// import { getAssignedCenterId } from "../../utils/userSession";

// const BASE_URL = "https://mis.empowerplus.in";

// export default function MobilizationManagementPage() {
//     const token = localStorage.getItem("token");
//     const assignedCenterId = getAssignedCenterId();
//     const selectedProjectId = getStoredContext()?.project || "";

//     /* ================= COMMON ================= */
//     const [msg, setMsg] = useState({ type: "", text: "" });
//     const flash = (type, text) => {
//         setMsg({ type, text });
//     };

//     const authHeaders = useMemo(
//         () => ({ Authorization: `Bearer ${token}` }),
//         [token]
//     );

//     const jsonHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//         }),
//         [token]
//     );

//     const safeJson = async (res) => {
//         const t = await res.text();
//         try {
//             return t ? JSON.parse(t) : [];
//         } catch {
//             return [];
//         }
//     };

//     /* ================= EXCEL UPLOAD ================= */
//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);
//     const [uploadProgress, setUploadProgress] = useState(null);
//     /* uploadProgress: { uploaded, total, chunk, totalChunks } | null */

//     const CHUNK_SIZE = 5000;
//     const PARALLEL = 6;   /* concurrent uploads at a time */

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Select Excel file");

//         /* ── Parse Excel on client as array-of-arrays (no Excel reconstruction needed) ── */
//         let dataRows;
//         try {
//             const buffer = await file.arrayBuffer();
//             const wb = XLSX.read(buffer, { type: "array" });
//             const ws = wb.Sheets[wb.SheetNames[0]];
//             const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
//             dataRows = raw.slice(1); // skip header row
//         } catch (err) {
//             return flash("err", `Cannot read file: ${err.message}`);
//         }

//         if (!dataRows.length) return flash("err", "Excel file is empty");

//         const totalRows = dataRows.length;
//         const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

//         setUploading(true);
//         setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

//         /* shared mutable counters (updated inside parallel tasks) */
//         let uploadedSoFar = 0;
//         let totalInserted = 0;
//         let totalSkipped = 0;
//         let failed = false;
//         let firstError = "";

//         /* sends one JSON chunk directly — no Excel reconstruction */
//         const uploadOneChunk = async (i) => {
//             const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);

//             const res = await fetch(
//                 `${BASE_URL}/api/mobilizations/upload-json?actor=Rahul`,
//                 {
//                     method: "POST",
//                     headers: { ...authHeaders, "Content-Type": "application/json" },
//                     body: JSON.stringify(chunkRows),
//                 }
//             );

//             if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);

//             const text = await res.text();
//             let data;
//             try { data = JSON.parse(text); } catch { data = text; }

//             if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);

//             const inserted = data?.inserted ?? 0;
//             const skipped = data?.skipped ?? 0;
//             const skippedRows = data?.skippedRows ?? [];

//             console.log(`[Chunk ${i + 1}/${totalChunks}] Inserted: ${inserted} | Skipped: ${skipped}`);
//             if (skippedRows.length > 0) {
//                 console.group(`[Chunk ${i + 1}] Skipped rows (${skippedRows.length}):`);
//                 skippedRows.forEach(reason => console.log(reason));
//                 console.groupEnd();
//             }

//             return { inserted, skipped };
//         };

//         /* process in batches of PARALLEL concurrent requests */
//         for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
//             const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
//             const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);

//             setUploadProgress({
//                 uploaded: uploadedSoFar,
//                 total: totalRows,
//                 chunk: batchEnd,
//                 totalChunks,
//             });

//             const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));

//             for (const result of results) {
//                 if (result.status === "fulfilled") {
//                     totalInserted += result.value.inserted;
//                     totalSkipped += result.value.skipped;
//                     uploadedSoFar += result.value.inserted + result.value.skipped;
//                 } else {
//                     failed = true;
//                     firstError = result.reason?.message || "Upload failed";
//                 }
//             }

//             setUploadProgress({
//                 uploaded: uploadedSoFar,
//                 total: totalRows,
//                 chunk: batchEnd,
//                 totalChunks,
//                 error: failed,
//             });
//         }

//         setUploading(false);

//         if (!failed) {
//             console.log(`[Upload Summary] Total Inserted: ${totalInserted} | Total Skipped: ${totalSkipped} | Total Rows: ${totalRows}`);
//             flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
//             setFile(null);
//             setUploadProgress(null);
//             fetchAllMobilizations();
//         } else {
//             flash("err", firstError);
//             setUploadProgress(prev => prev ? { ...prev, error: true } : null);
//         }
//     };

//     /* ================= TABLE ================= */
//     const [rows, setRows] = useState([]);
//     const [courseOptions, setCourseOptions] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [loadingAll, setLoadingAll] = useState(false);
//     const [allLoaded, setAllLoaded] = useState(false);
//     const [search, setSearch] = useState("");
//     const [visibleCount, setVisibleCount] = useState(100);

//     const resolveScopedUrl = (resource) => {
//         if (!shouldUseCenterScopedApi() || !assignedCenterId) {
//             return getCenterScopedUrl(BASE_URL, `/api/${resource}`);
//         }

//         if (selectedProjectId) {
//             if (resource === "mobilizations") {
//                 return centerApis.mobilizationsByProject(assignedCenterId, selectedProjectId);
//             }

//             if (resource === "courses") {
//                 return centerApis.coursesByProject(assignedCenterId, selectedProjectId);
//             }
//         }

//         if (resource === "mobilizations") {
//             return centerApis.mobilizations(assignedCenterId);
//         }

//         if (resource === "courses") {
//             return centerApis.courses(assignedCenterId);
//         }

//         return getCenterScopedUrl(BASE_URL, `/api/${resource}`);
//     };

//     const fetchFirst100 = async () => {
//         setLoading(true);
//         setAllLoaded(false);
//         try {
//             let items;
//             if (!shouldUseCenterScopedApi() || !assignedCenterId) {
//                 // Admin: use page endpoint — returns only 100 rows instantly
//                 const res = await fetch(
//                     `${BASE_URL}/api/mobilizations/page?page=0&size=100`,
//                     { headers: authHeaders }
//                 );
//                 const json = await safeJson(res);
//                 items = Array.isArray(json) ? json : (json.content ?? []);
//             } else {
//                 // Center-scoped user: their subset is small, fetch all
//                 const res = await fetch(resolveScopedUrl("mobilizations"), { headers: authHeaders });
//                 items = await safeJson(res);
//                 setAllLoaded(true);
//             }
//             setRows(filterRowsForCurrentScope(Array.isArray(items) ? items : []));
//         } catch {
//             setRows([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const fetchAllMobilizations = async () => {
//         setLoadingAll(true);
//         try {
//             const res = await fetch(resolveScopedUrl("mobilizations"), { headers: authHeaders });
//             const all = filterRowsForCurrentScope(await safeJson(res));
//             setRows(all);
//             setAllLoaded(true);
//             setVisibleCount(all.length);
//         } catch {
//             // keep existing rows on failure
//         } finally {
//             setLoadingAll(false);
//         }
//     };

//     useEffect(() => {
//         fetchFirst100();
//     }, []);

//     const fetchCourses = async () => {
//         try {
//             const res = await fetch(resolveScopedUrl("courses"), {
//                 headers: authHeaders,
//             });
//             const data = await safeJson(res);
//             setCourseOptions(Array.isArray(data) ? data : []);
//         } catch {
//             setCourseOptions([]);
//         }
//     };

//     useEffect(() => {
//         fetchCourses();
//     }, [selectedProjectId]);

//     useEffect(() => { setVisibleCount(100); }, [search]);

//     const filtered = useMemo(() => {
//         const q = search.toLowerCase();
//         return rows.filter(
//             (r) =>
//                 r.beneficiaryId?.toLowerCase().includes(q) ||
//                 r.name?.toLowerCase().includes(q) ||
//                 r.projectName?.toLowerCase().includes(q)
//         );
//     }, [rows, search]);

//     /* ================= EDIT ================= */
//     const emptyForm = {
//         beneficiaryId: "",

//         // department: "",
//         dateOfMobilization: "",

//         centreName: "",
//         projectName: "",

//         mobilizationType: "",
//         mobilizerName: "",
//         areaLocation: "",

//         salutation: "",
//         name: "",
//         gender: "",
//         age: "",

//         mobileNo: "",
//         whatsappNumber: "",
//         aadharNumber: "",
//         emailId: "",

//         highestEducationQualification: "",
//         guardianName: "",

//         workExperience: "",
//         willingToTakeJob: "",
//         willingToMigrate: "",

//         enrolledLast6Months: "",
//         interestedCourseName: "",
//         hearAboutUs: "",
//         counselled: "",
//         rocVerificationStatus: ""
//     };


//     const [editingId, setEditingId] = useState(null);
//     const [form, setForm] = useState(emptyForm);
//     const openEdit = (row) => {
//         setEditingId(row.beneficiaryId);

//         setForm({
//             beneficiaryId: row.beneficiaryId ?? "",

//             // department: row.department ?? "",
//             dateOfMobilization: row.dateOfMobilization ?? "",

//             centreName: row.centreName ?? "",
//             projectName: row.projectName ?? "",

//             mobilizationType: row.mobilizationType ?? "",
//             mobilizerName: row.mobilizerName ?? "",
//             areaLocation: row.areaLocation ?? "",

//             salutation: row.salutation ?? "",
//             name: row.name ?? "",
//             gender: row.gender ?? "",
//             age: row.age ?? "",

//             mobileNo: row.mobileNo ?? "",
//             whatsappNumber: row.whatsappNumber ?? "",
//             aadharNumber: row.aadharNumber ?? "",
//             emailId: row.emailId ?? "",

//             highestEducationQualification: row.highestEducationQualification ?? "",
//             guardianName: row.guardianName ?? "",

//             workExperience: row.workExperience ?? "",
//             willingToTakeJob: row.willingToTakeJob ?? "",
//             willingToMigrate: row.willingToMigrate ?? "",

//             enrolledLast6Months: row.enrolledLast6Months ?? "",
//             interestedCourseName: row.interestedCourseName ?? "",
//             hearAboutUs: row.hearAboutUs ?? "",
//             counselled: row.counselled ?? "",
//             rocVerificationStatus: row.rocVerificationStatus ?? ""
//         });
//     };

//     const updateMobilization = async () => {
//         await fetch(
//             `${BASE_URL}/api/mobilizations?beneficiaryId=${encodeURIComponent(
//                 editingId
//             )}&actor=Rahul`,
//             {
//                 method: "PUT",
//                 headers: jsonHeaders,
//                 body: JSON.stringify(form),
//             }
//         );
//         flash("ok", "Mobilization updated");
//         setEditingId(null);
//         fetchAllMobilizations();
//     };

//     /* ================= DELETE ================= */
//     const deleteMobilization = async (beneficiaryId) => {
//         if (!window.confirm("Deactivate this mobilization?")) return;

//         await fetch(
//             `${BASE_URL}/api/mobilizations?beneficiaryId=${encodeURIComponent(
//                 beneficiaryId
//             )}&actor=Rahul`,
//             {
//                 method: "DELETE",
//                 headers: authHeaders,
//             }
//         );
//         flash("ok", "Mobilization deleted");
//         fetchAllMobilizations();
//     };

//     /* ================= AUDIT ================= */
//     const [auditOpen, setAuditOpen] = useState(false);
//     const [auditRows, setAuditRows] = useState([]);
//     const [auditBeneficiary, setAuditBeneficiary] = useState("");

//     const openAuditAll = async () => {
//         setAuditOpen(true);
//         setAuditBeneficiary("");
//         const res = await fetch(
//             `${BASE_URL}/api/mobilization-audits`,
//             { headers: authHeaders }
//         );
//         setAuditRows(await safeJson(res));
//     };

//     const openAuditByBeneficiary = async (beneficiaryId) => {
//         setAuditOpen(true);
//         setAuditBeneficiary(beneficiaryId);
//         const res = await fetch(
//             `${BASE_URL}/api/mobilization-audits/by-beneficiary?beneficiaryId=${encodeURIComponent(
//                 beneficiaryId
//             )}`,
//             { headers: authHeaders }
//         );
//         setAuditRows(await safeJson(res));
//     };


//     const FormField = ({ label, value, onChange, type = "text" }) => (
//         <div className="field">
//             <label>{label}</label>
//             <input
//                 type={type}
//                 value={value || ""}
//                 onChange={(e) => onChange(e.target.value)}
//             />
//         </div>
//     );

//     const [exporting, setExporting] = useState(false);

//     const exportExcel = async () => {
//         if (exporting) return;
//         setExporting(true);
//         try {
//             let exportRows = rows;
//             if (!allLoaded) {
//                 const res = await fetch(resolveScopedUrl("mobilizations"), { headers: authHeaders });
//                 exportRows = filterRowsForCurrentScope(await safeJson(res));
//                 setRows(exportRows);
//                 setAllLoaded(true);
//                 setVisibleCount(exportRows.length);
//             }
//             if (!exportRows.length) { flash("err", "No data to export"); return; }
//             const ws = XLSX.utils.json_to_sheet(exportRows);
//             const wb = XLSX.utils.book_new();
//             XLSX.utils.book_append_sheet(wb, ws, "Mobilizations");
//             XLSX.writeFile(wb, "mobilizations.xlsx");
//         } finally {
//             setExporting(false);
//         }
//     };

//     const downloadTemplate = () => {
//         const a = document.createElement("a");
//         a.href = "/templates/mobilization_template.xlsx"; // local path
//         a.download = "mobilization_template.xlsx";
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//     };


//     /* ================= UI ================= */
//     return (
//         <div className="cmp-page">
//             <div className="header">
//                 <h1>Mobilization Management</h1>
//                 {/* <button className="btn" onClick={openAuditAll}>
//                     View All Audits
//                 </button> */}
//             </div>

//             {msg.text && (
//                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//                     <span>{msg.text}</span>
//                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
//                 </div>
//             )}

//             {/* Chunk upload progress */}
//             {uploadProgress && (
//                 <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
//                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//                         <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
//                             {uploadProgress.error
//                                 ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
//                                 : uploading
//                                     ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
//                                     : `Upload complete`}
//                         </span>
//                         <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
//                             {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
//                             &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
//                         </span>
//                     </div>
//                     <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
//                         <div style={{
//                             height: "100%",
//                             width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
//                             background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
//                             borderRadius: 999,
//                             transition: "width 0.3s ease",
//                         }} />
//                     </div>
//                     <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
//                         {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
//                     </div>
//                 </div>
//             )}

//             {/* Upload */}
//             <div className="card grid-3">
//                 <input type="file" onChange={(e) => setFile(e.target.files[0])} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }} />
//                 <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
//                     {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
//                 </button>

//                 <button className="btn btn-outline" onClick={downloadTemplate}>
//                     Download Excel Template
//                 </button>
//             </div>

//             {/* Table */}
//             <div className="card">
//                 <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
//                     <input
//                         className="search"
//                         placeholder="Search beneficiary / name / project..."
//                         value={search}
//                         onChange={(e) => setSearch(e.target.value)}
//                         style={{ flex: "0 0 70%", marginBottom: 0 }}
//                     />
//                     <button
//                         className="btn btn-outline"
//                         onClick={exportExcel}
//                         style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
//                         disabled={exporting}
//                     >
//                         {exporting ? "Processing..." : "Export All Data"}
//                     </button>
//                 </div>

//                 <div className="table-wrap">
//                     <table className="ep-table">
//                         <thead>
//                             <tr>
//                                 <th>#</th>
//                                 <th>Beneficiary ID</th>

//                                 {/* <th>Department</th> */}
//                                 <th>Date Of Mobilization</th>



//                                 <th>Mobilization Type</th>
//                                 <th>Mobilizer Name</th>
//                                 <th>Area / Location</th>

//                                 <th>Salutation</th>
//                                 <th>Name</th>
//                                 <th>Gender</th>
//                                 <th>Age</th>

//                                 <th>Mobile No</th>
//                                 <th>WhatsApp No</th>
//                                 <th>Aadhar No</th>
//                                 <th>Email</th>

//                                 <th>Highest Education</th>
//                                 <th>Guardian Name</th>

//                                 <th>Work Experience</th>
//                                 <th>Willing To Take Job</th>
//                                 <th>Willing To Migrate</th>

//                                 <th>Enrolled Last 6 Months</th>
//                                 <th>Interested Course</th>
//                                 <th>Heard About Us</th>
//                                 <th>Counselled</th>
//                                 {/* <th>ROC Verification</th> */}

//                                 <th className="actions">Actions</th>
//                             </tr>
//                         </thead>

//                         <tbody>
//                             {filtered.slice(0, visibleCount).map((r, i) => (
//                                 <tr key={r.beneficiaryId}>
//                                     <td>{i + 1}</td>
//                                     <td>{r.beneficiaryId}</td>

//                                     <td>{r.dateOfMobilization}</td>



//                                     <td>{r.mobilizationType}</td>
//                                     <td>{r.mobilizerName}</td>
//                                     <td>{r.areaLocation}</td>

//                                     <td>{r.salutation}</td>
//                                     <td>{r.name}</td>
//                                     <td>{r.gender}</td>
//                                     <td>{r.age}</td>

//                                     <td>{r.mobileNo}</td>
//                                     <td>{r.whatsappNumber}</td>
//                                     <td>{r.aadharNumber}</td>
//                                     <td>{r.emailId}</td>

//                                     <td>{r.highestEducationQualification}</td>
//                                     <td>{r.guardianName}</td>

//                                     <td>{r.workExperience}</td>
//                                     <td>{r.willingToTakeJob}</td>
//                                     <td>{r.willingToMigrateOtherState}</td>

//                                     <td>{r.enrolledLast6Months}</td>
//                                     <td>{r.interestedCourseName}</td>
//                                     <td>{r.hearAboutUs}</td>
//                                     <td>{r.counselled}</td>
//                                     {/* <td>{r.rocVerificationStatus}</td> */}

//                                     <td className="actions">
//                                         {/* <button className="btn btn-sm" onClick={() => openEdit(r)}>
//                                             Edit
//                                         </button> */}
//                                         {/* <button
//                                             className="btn btn-sm"
//                                             onClick={() => openAuditByBeneficiary(r.beneficiaryId)}
//                                         >
//                                             Audit
//                                         </button> */}
//                                         <button
//                                             className="btn btn-sm btn-danger"
//                                             onClick={() => deleteMobilization(r.beneficiaryId)}
//                                         >
//                                             Deactivate
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))}

//                             {!filtered.length && (
//                                 <tr>
//                                     <td colSpan="27" className="muted">
//                                         {loading ? "Loading..." : "No mobilizations found"}
//                                     </td>
//                                 </tr>
//                             )}
//                             {!allLoaded && !loading && (
//                                 <tr>
//                                     <td colSpan="27" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button
//                                             className="btn btn-outline"
//                                             onClick={fetchAllMobilizations}
//                                             disabled={loadingAll}
//                                         >
//                                             {loadingAll ? "Loading all data…" : "Load All Data"}
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                             {allLoaded && filtered.length > visibleCount && (
//                                 <tr>
//                                     <td colSpan="27" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button
//                                             className="btn btn-outline"
//                                             onClick={() => setVisibleCount((c) => c + 100)}
//                                         >
//                                             Show More ({filtered.length - visibleCount} remaining)
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                         </tbody>

//                     </table>
//                 </div>
//             </div>

//             {/* EDIT MODAL */}
//             {editingId &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setEditingId(null)}>
//                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
//                             <h2>Edit Mobilization</h2>

//                             <div className="form-grid">

//                                 <FormField label="Department" value={form.department}
//                                     onChange={(v) => setForm(p => ({ ...p, department: v }))} />

//                                 <FormField label="Date Of Mobilization" type="date"
//                                     value={form.dateOfMobilization}
//                                     onChange={(v) => setForm(p => ({ ...p, dateOfMobilization: v }))} />

//                                 <FormField label="Centre Name" value={form.centreName}
//                                     onChange={(v) => setForm(p => ({ ...p, centreName: v }))} />

//                                 <FormField label="Project Name" value={form.projectName}
//                                     onChange={(v) => setForm(p => ({ ...p, projectName: v }))} />

//                                 <FormField label="Mobilization Type" value={form.mobilizationType}
//                                     onChange={(v) => setForm(p => ({ ...p, mobilizationType: v }))} />

//                                 <FormField label="Mobilizer Name" value={form.mobilizerName}
//                                     onChange={(v) => setForm(p => ({ ...p, mobilizerName: v }))} />

//                                 <FormField label="Name" value={form.name}
//                                     onChange={(v) => setForm(p => ({ ...p, name: v }))} />

//                                 <FormField label="Gender" value={form.gender}
//                                     onChange={(v) => setForm(p => ({ ...p, gender: v }))} />

//                                 <FormField label="Age" type="number" value={form.age}
//                                     onChange={(v) => setForm(p => ({ ...p, age: v }))} />

//                                 <FormField label="Mobile No" value={form.mobileNo}
//                                     onChange={(v) => setForm(p => ({ ...p, mobileNo: v }))} />

//                                 <FormField label="WhatsApp Number" value={form.whatsappNumber}
//                                     onChange={(v) => setForm(p => ({ ...p, whatsappNumber: v }))} />

//                                 <FormField label="Email ID" value={form.emailId}
//                                     onChange={(v) => setForm(p => ({ ...p, emailId: v }))} />

//                                 <div className="field">
//                                     <label>Interested Course</label>
//                                     <select
//                                         value={form.interestedCourseName || ""}
//                                         onChange={(e) => setForm((p) => ({ ...p, interestedCourseName: e.target.value }))}
//                                     >
//                                         <option value="">Select Course</option>
//                                         {courseOptions.map((course) => (
//                                             <option key={course.courseId} value={course.courseName || course.courseId}>
//                                                 {course.courseName || course.courseId}
//                                             </option>
//                                         ))}
//                                     </select>
//                                 </div>
//                                 {/*
//                                 <FormField label="ROC Verification Status" value={form.rocVerificationStatus}
//                                     onChange={(v) => setForm(p => ({ ...p, rocVerificationStatus: v }))} /> */}

//                             </div>

//                             <div className="modal-actions">
//                                 <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
//                                 <button className="btn btn-primary" onClick={updateMobilization}>Save</button>
//                             </div>
//                         </div>
//                     </div>,
//                     document.body
//                 )
//             }


//             {/* AUDIT MODAL */}
//             {auditOpen &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
//                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
//                             <h2>Mobilization Audit Logs</h2>
//                             {auditBeneficiary && (
//                                 <div className="muted">
//                                     Beneficiary: <b>{auditBeneficiary}</b>
//                                 </div>
//                             )}
//                             <pre>{JSON.stringify(auditRows, null, 2)}</pre>
//                         </div>
//                     </div>,
//                     document.body
//                 )}

//             {/* INLINE CSS */}
//             <style>{`
//         .input-file {
//                  border: "1px solid #e5e7eb",
//         borderRadius: 10,
//         padding: "10px 12px",
//         background: "#fff",}
//             .table-wrap {
//   width: 100%;
//   overflow-x: auto;
//   -webkit-overflow-scrolling: touch;
// }

// .ep-table {
//   width: max-content;      /* KEY */
//   min-width: 2600px;       /* enough for all columns */
//   border-collapse: collapse;
// }

// .ep-table th,
// .ep-table td {
//   padding: 10px 12px;
//   border-bottom: 1px solid #e5e7eb;
//   font-size: 13px;
//   white-space: nowrap;     /* PREVENT WRAP */
// }

// .ep-table th {
//   background: #fcfcff;
//   position: sticky;
//   top: 0;
//   z-index: 2;
// }

// /* Sticky Actions Column */
// .ep-table td.actions,
// .ep-table th.actions {
//   position: sticky;
//   right: 0;
//   background: #fff;
//   box-shadow: -6px 0 8px rgba(0,0,0,0.05);
//   z-index: 3;
// }



//       .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
//       .header { display:flex; justify-content:space-between; align-items:center }
//       h1 { font-size:26px; font-weight:900 }

//       .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
//       .flash-ok { background:#ecfdf5; color:#065f46 }
//       .flash-err { background:#fef2f2; color:#991b1b }

//       .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
//       .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

//       .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
//       .btn-primary { background:#2563eb; color:#fff }
//       .btn-sm { padding:6px 10px; font-size:12px }
//       .btn-danger { color:#991b1b; border-color:#fca5a5 }

//       .search { margin-bottom:10px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

//       .table-wrap { overflow-x:auto }
//       .ep-table { width:100%; min-width:1200px; border-collapse:collapse }
//       th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
//       th { background:#fcfcff; text-align:left }
//       td.actions { position:sticky; right:0; background:#fff }

//       .muted { color:#6b7280; font-weight:700 }

//       .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
//       .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
//       .ep-modal.wide { width:1000px }

//       .form-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:12px }
//       .field { display:flex; flex-direction:column; gap:6px }
//       .field label { font-size:11px; font-weight:800; color:#6b7280 }
//       .field input, .field select { padding:10px; border-radius:10px; border:1px solid #e5e7eb }

//       .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
//       `}</style>
//         </div>
//     );
// }


// // import React, { useEffect, useMemo, useState } from "react";
// // import { createPortal } from "react-dom";
// // import * as XLSX from "xlsx";
// // import { filterRowsForCurrentScope, getCenterScopedUrl, centerApis, shouldUseCenterScopedApi } from "../../utils/centerScope";
// // import { getStoredContext } from "../../utils/contextStorage";
// // import { getAssignedCenterId } from "../../utils/userSession";

// // const BASE_URL = "https://mis.empowerplus.in";

// // export default function MobilizationManagementPage() {
// //     const token = localStorage.getItem("token");
// //     const assignedCenterId = getAssignedCenterId();
// //     const selectedProjectId = getStoredContext()?.project || "";

// //     /* ================= COMMON ================= */
// //     const [msg, setMsg] = useState({ type: "", text: "" });
// //     const flash = (type, text) => {
// //         setMsg({ type, text });
// //     };

// //     const authHeaders = useMemo(
// //         () => ({ Authorization: `Bearer ${token}` }),
// //         [token]
// //     );

// //     const jsonHeaders = useMemo(
// //         () => ({
// //             Authorization: `Bearer ${token}`,
// //             "Content-Type": "application/json",
// //         }),
// //         [token]
// //     );

// //     const safeJson = async (res) => {
// //         const t = await res.text();
// //         try {
// //             return t ? JSON.parse(t) : [];
// //         } catch {
// //             return [];
// //         }
// //     };

// //     /* ================= EXCEL UPLOAD ================= */
// //     const [file, setFile] = useState(null);
// //     const [uploading, setUploading] = useState(false);
// //     const [uploadProgress, setUploadProgress] = useState(null);
// //     /* uploadProgress: { uploaded, total, chunk, totalChunks } | null */

// //     const CHUNK_SIZE = 500;
// //     const PARALLEL   = 4;   /* concurrent uploads at a time */

// //     const uploadExcel = async () => {
// //         if (!file) return flash("err", "Select Excel file");

// //         /* ── Parse Excel on client ── */
// //         let allRows;
// //         try {
// //             const buffer = await file.arrayBuffer();
// //             const wb = XLSX.read(buffer, { type: "array" });
// //             const ws = wb.Sheets[wb.SheetNames[0]];
// //             allRows = XLSX.utils.sheet_to_json(ws, { defval: "" });
// //         } catch (err) {
// //             return flash("err", `Cannot read file: ${err.message}`);
// //         }

// //         if (!allRows.length) return flash("err", "Excel file is empty");

// //         const totalRows   = allRows.length;
// //         const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

// //         setUploading(true);
// //         setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

// //         /* shared mutable counters (updated inside parallel tasks) */
// //         let uploadedSoFar = 0;
// //         let failed        = false;
// //         let firstError    = "";

// //         /* builds and sends one chunk, returns rows count on success */
// //         const uploadOneChunk = async (i) => {
// //             const chunkRows = allRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);

// //             const chunkWs  = XLSX.utils.json_to_sheet(chunkRows);
// //             const chunkWb  = XLSX.utils.book_new();
// //             XLSX.utils.book_append_sheet(chunkWb, chunkWs, "Sheet1");
// //             const arr  = XLSX.write(chunkWb, { bookType: "xlsx", type: "array" });
// //             const blob = new Blob([arr], {
// //                 type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
// //             });

// //             const fd = new FormData();
// //             fd.append("file", blob, `chunk_${i + 1}.xlsx`);

// //             const res = await fetch(
// //                 `${BASE_URL}/api/mobilizations/upload?actor=Rahul`,
// //                 { method: "POST", headers: authHeaders, body: fd }
// //             );

// //             if (res.status === 413) throw new Error(`Chunk ${i + 1}: File too large (413)`);

// //             const text = await res.text();
// //             let data;
// //             try { data = JSON.parse(text); } catch { data = text; }

// //             if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);

// //             return chunkRows.length;
// //         };

// //         /* process in batches of PARALLEL concurrent requests */
// //         for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
// //             const batchEnd     = Math.min(batchStart + PARALLEL, totalChunks);
// //             const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);

// //             setUploadProgress({
// //                 uploaded: uploadedSoFar,
// //                 total: totalRows,
// //                 chunk: batchEnd,        /* show highest chunk in this batch */
// //                 totalChunks,
// //             });

// //             const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));

// //             for (const result of results) {
// //                 if (result.status === "fulfilled") {
// //                     uploadedSoFar += result.value;
// //                 } else {
// //                     failed     = true;
// //                     firstError = result.reason?.message || "Upload failed";
// //                 }
// //             }

// //             setUploadProgress({
// //                 uploaded: uploadedSoFar,
// //                 total: totalRows,
// //                 chunk: batchEnd,
// //                 totalChunks,
// //                 error: failed,
// //             });
// //         }

// //         setUploading(false);

// //         if (!failed) {
// //             flash("ok", `Upload complete — ${uploadedSoFar.toLocaleString()} rows uploaded (${totalChunks} chunks, ${PARALLEL} parallel)`);
// //             setFile(null);
// //             setUploadProgress(null);
// //             fetchMobilizations();
// //         } else {
// //             flash("err", firstError);
// //             setUploadProgress(prev => prev ? { ...prev, error: true } : null);
// //         }
// //     };

// //     /* ================= TABLE ================= */
// //     const [rows, setRows] = useState([]);
// //     const [courseOptions, setCourseOptions] = useState([]);
// //     const [loading, setLoading] = useState(false);
// //     const [search, setSearch] = useState("");
// //     const [visibleCount, setVisibleCount] = useState(100);

// //     const resolveScopedUrl = (resource) => {
// //         if (!shouldUseCenterScopedApi() || !assignedCenterId) {
// //             return getCenterScopedUrl(BASE_URL, `/api/${resource}`);
// //         }

// //         if (selectedProjectId) {
// //             if (resource === "mobilizations") {
// //                 return centerApis.mobilizationsByProject(assignedCenterId, selectedProjectId);
// //             }

// //             if (resource === "courses") {
// //                 return centerApis.coursesByProject(assignedCenterId, selectedProjectId);
// //             }
// //         }

// //         if (resource === "mobilizations") {
// //             return centerApis.mobilizations(assignedCenterId);
// //         }

// //         if (resource === "courses") {
// //             return centerApis.courses(assignedCenterId);
// //         }

// //         return getCenterScopedUrl(BASE_URL, `/api/${resource}`);
// //     };

// //     const fetchMobilizations = async () => {
// //         setLoading(true);
// //         try {
// //             const res = await fetch(resolveScopedUrl("mobilizations"), {
// //                 headers: authHeaders,
// //             });

// //             setRows(filterRowsForCurrentScope(await safeJson(res)));
// //         } catch {
// //             setRows([]);
// //         } finally {
// //             setLoading(false);
// //         }
// //     };

// //     useEffect(() => {
// //         fetchMobilizations();
// //     }, []);

// //     const fetchCourses = async () => {
// //         try {
// //             const res = await fetch(resolveScopedUrl("courses"), {
// //                 headers: authHeaders,
// //             });
// //             const data = await safeJson(res);
// //             setCourseOptions(Array.isArray(data) ? data : []);
// //         } catch {
// //             setCourseOptions([]);
// //         }
// //     };

// //     useEffect(() => {
// //         fetchCourses();
// //     }, [selectedProjectId]);

// //     useEffect(() => { setVisibleCount(100); }, [search]);

// //     const filtered = useMemo(() => {
// //         const q = search.toLowerCase();
// //         return rows.filter(
// //             (r) =>
// //                 r.beneficiaryId?.toLowerCase().includes(q) ||
// //                 r.name?.toLowerCase().includes(q) ||
// //                 r.projectName?.toLowerCase().includes(q)
// //         );
// //     }, [rows, search]);

// //     /* ================= EDIT ================= */
// //     const emptyForm = {
// //         beneficiaryId: "",

// //         // department: "",
// //         dateOfMobilization: "",

// //         centreName: "",
// //         projectName: "",

// //         mobilizationType: "",
// //         mobilizerName: "",
// //         areaLocation: "",

// //         salutation: "",
// //         name: "",
// //         gender: "",
// //         age: "",

// //         mobileNo: "",
// //         whatsappNumber: "",
// //         aadharNumber: "",
// //         emailId: "",

// //         highestEducationQualification: "",
// //         guardianName: "",

// //         workExperience: "",
// //         willingToTakeJob: "",
// //         willingToMigrate: "",

// //         enrolledLast6Months: "",
// //         interestedCourseName: "",
// //         hearAboutUs: "",
// //         counselled: "",
// //         rocVerificationStatus: ""
// //     };


// //     const [editingId, setEditingId] = useState(null);
// //     const [form, setForm] = useState(emptyForm);
// //     const openEdit = (row) => {
// //         setEditingId(row.beneficiaryId);

// //         setForm({
// //             beneficiaryId: row.beneficiaryId ?? "",

// //             // department: row.department ?? "",
// //             dateOfMobilization: row.dateOfMobilization ?? "",

// //             centreName: row.centreName ?? "",
// //             projectName: row.projectName ?? "",

// //             mobilizationType: row.mobilizationType ?? "",
// //             mobilizerName: row.mobilizerName ?? "",
// //             areaLocation: row.areaLocation ?? "",

// //             salutation: row.salutation ?? "",
// //             name: row.name ?? "",
// //             gender: row.gender ?? "",
// //             age: row.age ?? "",

// //             mobileNo: row.mobileNo ?? "",
// //             whatsappNumber: row.whatsappNumber ?? "",
// //             aadharNumber: row.aadharNumber ?? "",
// //             emailId: row.emailId ?? "",

// //             highestEducationQualification: row.highestEducationQualification ?? "",
// //             guardianName: row.guardianName ?? "",

// //             workExperience: row.workExperience ?? "",
// //             willingToTakeJob: row.willingToTakeJob ?? "",
// //             willingToMigrate: row.willingToMigrate ?? "",

// //             enrolledLast6Months: row.enrolledLast6Months ?? "",
// //             interestedCourseName: row.interestedCourseName ?? "",
// //             hearAboutUs: row.hearAboutUs ?? "",
// //             counselled: row.counselled ?? "",
// //             rocVerificationStatus: row.rocVerificationStatus ?? ""
// //         });
// //     };

// //     const updateMobilization = async () => {
// //         await fetch(
// //             `${BASE_URL}/api/mobilizations?beneficiaryId=${encodeURIComponent(
// //                 editingId
// //             )}&actor=Rahul`,
// //             {
// //                 method: "PUT",
// //                 headers: jsonHeaders,
// //                 body: JSON.stringify(form),
// //             }
// //         );
// //         flash("ok", "Mobilization updated");
// //         setEditingId(null);
// //         fetchMobilizations();
// //     };

// //     /* ================= DELETE ================= */
// //     const deleteMobilization = async (beneficiaryId) => {
// //         if (!window.confirm("Deactivate this mobilization?")) return;

// //         await fetch(
// //             `${BASE_URL}/api/mobilizations?beneficiaryId=${encodeURIComponent(
// //                 beneficiaryId
// //             )}&actor=Rahul`,
// //             {
// //                 method: "DELETE",
// //                 headers: authHeaders,
// //             }
// //         );
// //         flash("ok", "Mobilization deactivated");
// //         fetchMobilizations();
// //     };

// //     /* ================= AUDIT ================= */
// //     const [auditOpen, setAuditOpen] = useState(false);
// //     const [auditRows, setAuditRows] = useState([]);
// //     const [auditBeneficiary, setAuditBeneficiary] = useState("");

// //     const openAuditAll = async () => {
// //         setAuditOpen(true);
// //         setAuditBeneficiary("");
// //         const res = await fetch(
// //             `${BASE_URL}/api/mobilization-audits`,
// //             { headers: authHeaders }
// //         );
// //         setAuditRows(await safeJson(res));
// //     };

// //     const openAuditByBeneficiary = async (beneficiaryId) => {
// //         setAuditOpen(true);
// //         setAuditBeneficiary(beneficiaryId);
// //         const res = await fetch(
// //             `${BASE_URL}/api/mobilization-audits/by-beneficiary?beneficiaryId=${encodeURIComponent(
// //                 beneficiaryId
// //             )}`,
// //             { headers: authHeaders }
// //         );
// //         setAuditRows(await safeJson(res));
// //     };


// //     const FormField = ({ label, value, onChange, type = "text" }) => (
// //         <div className="field">
// //             <label>{label}</label>
// //             <input
// //                 type={type}
// //                 value={value || ""}
// //                 onChange={(e) => onChange(e.target.value)}
// //             />
// //         </div>
// //     );

// //     const [exporting, setExporting] = useState(false);

// //     const exportExcel = () => {
// //         if (!rows.length) return flash("err", "No data to export");
// //         if (exporting) return;
// //         setExporting(true);
// //         setTimeout(() => {
// //             try {
// //                 const ws = XLSX.utils.json_to_sheet(rows);
// //                 const wb = XLSX.utils.book_new();
// //                 XLSX.utils.book_append_sheet(wb, ws, "Mobilizations");
// //                 XLSX.writeFile(wb, "mobilizations.xlsx");
// //             } finally {
// //                 setExporting(false);
// //             }
// //         }, 50);
// //     };

// //     const downloadTemplate = () => {
// //         const a = document.createElement("a");
// //         a.href = "/templates/mobilization_template.xlsx"; // local path
// //         a.download = "mobilization_template.xlsx";
// //         document.body.appendChild(a);
// //         a.click();
// //         a.remove();
// //     };


// //     /* ================= UI ================= */
// //     return (
// //         <div className="cmp-page">
// //             <div className="header">
// //                 <h1>Mobilization Management</h1>
// //                 {/* <button className="btn" onClick={openAuditAll}>
// //                     View All Audits
// //                 </button> */}
// //             </div>

// //             {msg.text && (
// //                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
// //                     <span>{msg.text}</span>
// //                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
// //                 </div>
// //             )}

// //             {/* Chunk upload progress */}
// //             {uploadProgress && (
// //                 <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
// //                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
// //                         <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
// //                             {uploadProgress.error
// //                                 ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
// //                                 : uploading
// //                                     ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
// //                                     : `Upload complete`}
// //                         </span>
// //                         <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
// //                             {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
// //                             &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
// //                         </span>
// //                     </div>
// //                     <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
// //                         <div style={{
// //                             height: "100%",
// //                             width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
// //                             background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
// //                             borderRadius: 999,
// //                             transition: "width 0.3s ease",
// //                         }} />
// //                     </div>
// //                     <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
// //                         {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
// //                     </div>
// //                 </div>
// //             )}

// //             {/* Upload */}
// //             <div className="card grid-3">
// //                 <input type="file" onChange={(e) => setFile(e.target.files[0])} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }} />
// //                 <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
// //                     {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
// //                 </button>

// //                 <button className="btn btn-outline" onClick={downloadTemplate}>
// //                     Download Excel Template
// //                 </button>
// //             </div>

// //             {/* Table */}
// //             <div className="card">
// //                 <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
// //                     <input
// //                         className="search"
// //                         placeholder="Search beneficiary / name / project..."
// //                         value={search}
// //                         onChange={(e) => setSearch(e.target.value)}
// //                         style={{ flex: "0 0 70%", marginBottom: 0 }}
// //                     />
// //                     <button
// //                         className="btn btn-outline"
// //                         onClick={exportExcel}
// //                         style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
// //                         disabled={exporting}
// //                     >
// //                         {exporting ? "Processing..." : "Export All Data"}
// //                     </button>
// //                 </div>

// //                 <div className="table-wrap">
// //                     <table className="ep-table">
// //                         <thead>
// //                             <tr>
// //                                 <th>#</th>
// //                                 <th>Beneficiary ID</th>

// //                                 {/* <th>Department</th> */}
// //                                 <th>Date Of Mobilization</th>



// //                                 <th>Mobilization Type</th>
// //                                 <th>Mobilizer Name</th>
// //                                 <th>Area / Location</th>

// //                                 <th>Salutation</th>
// //                                 <th>Name</th>
// //                                 <th>Gender</th>
// //                                 <th>Age</th>

// //                                 <th>Mobile No</th>
// //                                 <th>WhatsApp No</th>
// //                                 <th>Aadhar No</th>
// //                                 <th>Email</th>

// //                                 <th>Highest Education</th>
// //                                 <th>Guardian Name</th>

// //                                 <th>Work Experience</th>
// //                                 <th>Willing To Take Job</th>
// //                                 <th>Willing To Migrate</th>

// //                                 <th>Enrolled Last 6 Months</th>
// //                                 <th>Interested Course</th>
// //                                 <th>Heard About Us</th>
// //                                 <th>Counselled</th>
// //                                 {/* <th>ROC Verification</th> */}

// //                                 <th className="actions">Actions</th>
// //                             </tr>
// //                         </thead>

// //                         <tbody>
// //                             {filtered.slice(0, visibleCount).map((r, i) => (
// //                                 <tr key={r.beneficiaryId}>
// //                                     <td>{i + 1}</td>
// //                                     <td>{r.beneficiaryId}</td>

// //                                     <td>{r.dateOfMobilization}</td>



// //                                     <td>{r.mobilizationType}</td>
// //                                     <td>{r.mobilizerName}</td>
// //                                     <td>{r.areaLocation}</td>

// //                                     <td>{r.salutation}</td>
// //                                     <td>{r.name}</td>
// //                                     <td>{r.gender}</td>
// //                                     <td>{r.age}</td>

// //                                     <td>{r.mobileNo}</td>
// //                                     <td>{r.whatsappNumber}</td>
// //                                     <td>{r.aadharNumber}</td>
// //                                     <td>{r.emailId}</td>

// //                                     <td>{r.highestEducationQualification}</td>
// //                                     <td>{r.guardianName}</td>

// //                                     <td>{r.workExperience}</td>
// //                                     <td>{r.willingToTakeJob}</td>
// //                                     <td>{r.willingToMigrateOtherState}</td>

// //                                     <td>{r.enrolledLast6Months}</td>
// //                                     <td>{r.interestedCourseName}</td>
// //                                     <td>{r.hearAboutUs}</td>
// //                                     <td>{r.counselled}</td>
// //                                     {/* <td>{r.rocVerificationStatus}</td> */}

// //                                     <td className="actions">
// //                                         {/* <button className="btn btn-sm" onClick={() => openEdit(r)}>
// //                                             Edit
// //                                         </button> */}
// //                                         {/* <button
// //                                             className="btn btn-sm"
// //                                             onClick={() => openAuditByBeneficiary(r.beneficiaryId)}
// //                                         >
// //                                             Audit
// //                                         </button> */}
// //                                         <button
// //                                             className="btn btn-sm btn-danger"
// //                                             onClick={() => deleteMobilization(r.beneficiaryId)}
// //                                         >
// //                                             Deactivate
// //                                         </button>
// //                                     </td>
// //                                 </tr>
// //                             ))}

// //                             {!filtered.length && (
// //                                 <tr>
// //                                     <td colSpan="27" className="muted">
// //                                         {loading ? "Loading..." : "No mobilizations found"}
// //                                     </td>
// //                                 </tr>
// //                             )}
// //                             {filtered.length > visibleCount && (
// //                                 <tr>
// //                                     <td colSpan="27" style={{ textAlign: "center", padding: "14px" }}>
// //                                         <button
// //                                             className="btn btn-outline"
// //                                             onClick={() => setVisibleCount((c) => c + 100)}
// //                                         >
// //                                             Load More ({filtered.length - visibleCount} remaining)
// //                                         </button>
// //                                     </td>
// //                                 </tr>
// //                             )}
// //                         </tbody>

// //                     </table>
// //                 </div>
// //             </div>

// //             {/* EDIT MODAL */}
// //             {editingId &&
// //                 createPortal(
// //                     <div className="modal-overlay" onClick={() => setEditingId(null)}>
// //                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
// //                             <h2>Edit Mobilization</h2>

// //                             <div className="form-grid">

// //                                 <FormField label="Department" value={form.department}
// //                                     onChange={(v) => setForm(p => ({ ...p, department: v }))} />

// //                                 <FormField label="Date Of Mobilization" type="date"
// //                                     value={form.dateOfMobilization}
// //                                     onChange={(v) => setForm(p => ({ ...p, dateOfMobilization: v }))} />

// //                                 <FormField label="Centre Name" value={form.centreName}
// //                                     onChange={(v) => setForm(p => ({ ...p, centreName: v }))} />

// //                                 <FormField label="Project Name" value={form.projectName}
// //                                     onChange={(v) => setForm(p => ({ ...p, projectName: v }))} />

// //                                 <FormField label="Mobilization Type" value={form.mobilizationType}
// //                                     onChange={(v) => setForm(p => ({ ...p, mobilizationType: v }))} />

// //                                 <FormField label="Mobilizer Name" value={form.mobilizerName}
// //                                     onChange={(v) => setForm(p => ({ ...p, mobilizerName: v }))} />

// //                                 <FormField label="Name" value={form.name}
// //                                     onChange={(v) => setForm(p => ({ ...p, name: v }))} />

// //                                 <FormField label="Gender" value={form.gender}
// //                                     onChange={(v) => setForm(p => ({ ...p, gender: v }))} />

// //                                 <FormField label="Age" type="number" value={form.age}
// //                                     onChange={(v) => setForm(p => ({ ...p, age: v }))} />

// //                                 <FormField label="Mobile No" value={form.mobileNo}
// //                                     onChange={(v) => setForm(p => ({ ...p, mobileNo: v }))} />

// //                                 <FormField label="WhatsApp Number" value={form.whatsappNumber}
// //                                     onChange={(v) => setForm(p => ({ ...p, whatsappNumber: v }))} />

// //                                 <FormField label="Email ID" value={form.emailId}
// //                                     onChange={(v) => setForm(p => ({ ...p, emailId: v }))} />

// //                                 <div className="field">
// //                                     <label>Interested Course</label>
// //                                     <select
// //                                         value={form.interestedCourseName || ""}
// //                                         onChange={(e) => setForm((p) => ({ ...p, interestedCourseName: e.target.value }))}
// //                                     >
// //                                         <option value="">Select Course</option>
// //                                         {courseOptions.map((course) => (
// //                                             <option key={course.courseId} value={course.courseName || course.courseId}>
// //                                                 {course.courseName || course.courseId}
// //                                             </option>
// //                                         ))}
// //                                     </select>
// //                                 </div>
// //                                 {/*
// //                                 <FormField label="ROC Verification Status" value={form.rocVerificationStatus}
// //                                     onChange={(v) => setForm(p => ({ ...p, rocVerificationStatus: v }))} /> */}

// //                             </div>

// //                             <div className="modal-actions">
// //                                 <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
// //                                 <button className="btn btn-primary" onClick={updateMobilization}>Save</button>
// //                             </div>
// //                         </div>
// //                     </div>,
// //                     document.body
// //                 )
// //             }


// //             {/* AUDIT MODAL */}
// //             {auditOpen &&
// //                 createPortal(
// //                     <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
// //                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
// //                             <h2>Mobilization Audit Logs</h2>
// //                             {auditBeneficiary && (
// //                                 <div className="muted">
// //                                     Beneficiary: <b>{auditBeneficiary}</b>
// //                                 </div>
// //                             )}
// //                             <pre>{JSON.stringify(auditRows, null, 2)}</pre>
// //                         </div>
// //                     </div>,
// //                     document.body
// //                 )}

// //             {/* INLINE CSS */}
// //             <style>{`
// //         .input-file {
// //                  border: "1px solid #e5e7eb",
// //         borderRadius: 10,
// //         padding: "10px 12px",
// //         background: "#fff",}
// //             .table-wrap {
// //   width: 100%;
// //   overflow-x: auto;
// //   -webkit-overflow-scrolling: touch;
// // }

// // .ep-table {
// //   width: max-content;      /* KEY */
// //   min-width: 2600px;       /* enough for all columns */
// //   border-collapse: collapse;
// // }

// // .ep-table th,
// // .ep-table td {
// //   padding: 10px 12px;
// //   border-bottom: 1px solid #e5e7eb;
// //   font-size: 13px;
// //   white-space: nowrap;     /* PREVENT WRAP */
// // }

// // .ep-table th {
// //   background: #fcfcff;
// //   position: sticky;
// //   top: 0;
// //   z-index: 2;
// // }

// // /* Sticky Actions Column */
// // .ep-table td.actions,
// // .ep-table th.actions {
// //   position: sticky;
// //   right: 0;
// //   background: #fff;
// //   box-shadow: -6px 0 8px rgba(0,0,0,0.05);
// //   z-index: 3;
// // }



// //       .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
// //       .header { display:flex; justify-content:space-between; align-items:center }
// //       h1 { font-size:26px; font-weight:900 }

// //       .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
// //       .flash-ok { background:#ecfdf5; color:#065f46 }
// //       .flash-err { background:#fef2f2; color:#991b1b }

// //       .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
// //       .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

// //       .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
// //       .btn-primary { background:#2563eb; color:#fff }
// //       .btn-sm { padding:6px 10px; font-size:12px }
// //       .btn-danger { color:#991b1b; border-color:#fca5a5 }

// //       .search { margin-bottom:10px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

// //       .table-wrap { overflow-x:auto }
// //       .ep-table { width:100%; min-width:1200px; border-collapse:collapse }
// //       th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
// //       th { background:#fcfcff; text-align:left }
// //       td.actions { position:sticky; right:0; background:#fff }

// //       .muted { color:#6b7280; font-weight:700 }

// //       .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
// //       .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
// //       .ep-modal.wide { width:1000px }

// //       .form-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:12px }
// //       .field { display:flex; flex-direction:column; gap:6px }
// //       .field label { font-size:11px; font-weight:800; color:#6b7280 }
// //       .field input, .field select { padding:10px; border-radius:10px; border:1px solid #e5e7eb }

// //       .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
// //       `}</style>
// //         </div>
// //     );
// // }
